import React, { useEffect, useState } from 'react';
import { Table, Typography, message, Empty, Spin } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { TrophyOutlined } from '@ant-design/icons';
import { TournamentService } from '@/services/tournament.service';
import { Tournament, Match } from '@/types/api.types';
import { logger } from '@/utils/logger';
import { ResultsContainer } from './styles/TeamResults.styles';

const { Title } = Typography;

interface TeamResult {
  teamName: string;
  gold: number;
  silver: number;
  bronze: number;
  total: number;
  place: number;
}

interface TeamResultsProps {
  tournamentId: number;
  tournament: Tournament;
}

export const TeamResults: React.FC<TeamResultsProps> = ({
  tournamentId,
}) => {
  const [teamResults, setTeamResults] = useState<TeamResult[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, [tournamentId]);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Загружаем категории
      const categoriesData = await TournamentService.getTournamentCategories(tournamentId);
      
      // Загружаем матчи для расчета результатов
      const matches = await TournamentService.getTournamentMatches(tournamentId);
      const matchesArray = Array.isArray(matches) ? matches : (matches as any)?.data || [];

      // Группируем матчи по категориям
      const matchesByCategory = new Map<number, Match[]>();
      matchesArray.forEach((match: Match) => {
        if (match.categoryId) {
          if (!matchesByCategory.has(match.categoryId)) {
            matchesByCategory.set(match.categoryId, []);
          }
          matchesByCategory.get(match.categoryId)!.push(match);
        }
      });

      // Собираем медали по командам
      const teamMedals = new Map<string, { gold: number; silver: number; bronze: number }>();

      for (const category of categoriesData) {
        const categoryMatches = matchesByCategory.get(category.id) || [];
        
        // Находим финальный матч (последний раунд)
        const maxRound = Math.max(...categoryMatches.map(m => m.round || 0), 0);
        const finalMatches = categoryMatches.filter(m => m.round === maxRound);
        
        // Находим матч за 3 место
        // Сначала ищем матч, который является consolationMatchNumber для полуфиналов
        const semiFinalMatches = categoryMatches.filter(m => m.round === maxRound - 1);
        let thirdPlaceMatch: Match | undefined = undefined;
        
        if (semiFinalMatches.length > 0) {
          // Находим consolationMatchNumber из полуфиналов
          const consolationMatchNumber = semiFinalMatches[0]?.consolationMatchNumber;
          if (consolationMatchNumber) {
            thirdPlaceMatch = categoryMatches.find(m => m.matchNumber === consolationMatchNumber);
          }
        }
        
        // Если не нашли через consolationMatchNumber, ищем матч в последнем раунде, который не является финалом
        if (!thirdPlaceMatch && maxRound > 1) {
          const lastRoundMatches = categoryMatches.filter(m => m.round === maxRound);
          if (lastRoundMatches.length > finalMatches.length) {
            // Есть матч в последнем раунде, который не является финалом - это матч за 3 место
            thirdPlaceMatch = lastRoundMatches.find(m => !finalMatches.includes(m));
          }
        }

        // Определяем места участников
        const places = new Map<number, number>(); // participantId -> place
        
        // Определяем 1 и 2 место из финала
        for (const finalMatch of finalMatches) {
          if (finalMatch.status === 'completed' && finalMatch.winnerParticipantId) {
            const winnerId = finalMatch.winnerParticipantId;
            const loserId = finalMatch.participant1Id === winnerId 
              ? finalMatch.participant2Id 
              : finalMatch.participant1Id;
            
            places.set(winnerId, 1);
            if (loserId) {
              places.set(loserId, 2);
            }
          }
        }

        // Определяем 3 место из матча за 3 место
        if (thirdPlaceMatch && thirdPlaceMatch.status === 'completed' && thirdPlaceMatch.winnerParticipantId) {
          places.set(thirdPlaceMatch.winnerParticipantId, 3);
          
          // Проигравший в матче за 3 место получает 4 место (не учитываем в медалях)
          const thirdPlaceLoserId = thirdPlaceMatch.participant1Id === thirdPlaceMatch.winnerParticipantId
            ? thirdPlaceMatch.participant2Id
            : thirdPlaceMatch.participant1Id;
          if (thirdPlaceLoserId) {
            places.set(thirdPlaceLoserId, 4);
          }
        } else if (maxRound > 1) {
          // Если нет отдельного матча за 3 место, оба проигравших в полуфинале получают 3 место
          for (const semiMatch of semiFinalMatches) {
            if (semiMatch.status === 'completed' && semiMatch.winnerParticipantId) {
              const loserId = semiMatch.participant1Id === semiMatch.winnerParticipantId
                ? semiMatch.participant2Id
                : semiMatch.participant1Id;
              if (loserId && !places.has(loserId)) {
                places.set(loserId, 3);
              }
            }
          }
        }

        // Подсчитываем медали по командам
        for (const [participantId, place] of places.entries()) {
          if (place > 3) continue; // Учитываем только призовые места

          // Находим информацию об участнике из матчей
          let participantInfo: any = null;
          for (const match of categoryMatches) {
            const p1Id = match.participant1?.id || (match.participant1 as any)?.participantId || match.participant1Id;
            const p2Id = match.participant2?.id || (match.participant2 as any)?.participantId || match.participant2Id;
            
            if (p1Id === participantId) {
              participantInfo = match.participant1;
              break;
            }
            if (p2Id === participantId) {
              participantInfo = match.participant2;
              break;
            }
          }

          if (participantInfo) {
            const teamName = (participantInfo.club as any)?.name || participantInfo.clubName || (participantInfo.clubParticipants as any)?.[0]?.club?.name || 'Без клуба';
            
            if (!teamMedals.has(teamName)) {
              teamMedals.set(teamName, { gold: 0, silver: 0, bronze: 0 });
            }
            
            const medals = teamMedals.get(teamName)!;
            if (place === 1) medals.gold++;
            else if (place === 2) medals.silver++;
            else if (place === 3) medals.bronze++;
          }
        }
      }

      // Преобразуем в массив и сортируем
      const results: TeamResult[] = Array.from(teamMedals.entries()).map(([teamName, medals]) => ({
        teamName,
        gold: medals.gold,
        silver: medals.silver,
        bronze: medals.bronze,
        total: medals.gold + medals.silver + medals.bronze,
        place: 0, // Будет установлено после сортировки
      }));

      // Сортируем по общему количеству медалей (золото > серебро > бронза)
      results.sort((a, b) => {
        if (a.total !== b.total) return b.total - a.total;
        if (a.gold !== b.gold) return b.gold - a.gold;
        if (a.silver !== b.silver) return b.silver - a.silver;
        return b.bronze - a.bronze;
      });

      // Определяем места (с учетом одинаковых результатов)
      let currentPlace = 1;
      for (let i = 0; i < results.length; i++) {
        if (i > 0) {
          const prev = results[i - 1];
          const curr = results[i];
          // Если результаты одинаковые, место остается прежним
          if (prev.total !== curr.total || 
              prev.gold !== curr.gold || 
              prev.silver !== curr.silver || 
              prev.bronze !== curr.bronze) {
            currentPlace = i + 1;
          }
        }
        results[i].place = currentPlace;
      }

      setTeamResults(results);
    } catch (error) {
      logger.error('Error fetching team results:', error);
      message.error('Ошибка при загрузке результатов команды');
      setTeamResults([]);
    } finally {
      setLoading(false);
    }
  };

  const formatPlace = (place: number, index: number, results: TeamResult[]): string => {
    // Проверяем, есть ли участники с таким же местом
    const samePlaceCount = results.filter(r => r.place === place).length;
    if (samePlaceCount > 1) {
      // Находим диапазон мест
      const firstIndex = results.findIndex(r => r.place === place);
      const lastIndex = firstIndex + samePlaceCount - 1;
      if (index === firstIndex && index === lastIndex) {
        return `${place}`;
      }
      return `${place}-${place + samePlaceCount - 1}`;
    }
    return `${place}`;
  };

  const columns: ColumnsType<TeamResult> = [
    {
      title: 'Место',
      dataIndex: 'place',
      key: 'place',
      width: 100,
      render: (place: number, _record: TeamResult, index: number) => {
        return (
          <span style={{ fontWeight: 'bold', fontSize: '16px' }}>
            {formatPlace(place, index, teamResults)}
          </span>
        );
      },
    },
    {
      title: 'Команда',
      dataIndex: 'teamName',
      key: 'teamName',
      render: (teamName: string) => <span style={{ fontSize: '16px' }}>{teamName}</span>,
    },
    {
      title: (
        <span>
          <TrophyOutlined style={{ color: '#ffd700', marginRight: 4 }} />
          Золото
        </span>
      ),
      dataIndex: 'gold',
      key: 'gold',
      width: 120,
      align: 'center',
      render: (gold: number) => (
        <span style={{ fontSize: '16px', fontWeight: 'bold' }}>
          <TrophyOutlined style={{ color: '#ffd700', marginRight: 4 }} />
          {gold}
        </span>
      ),
    },
    {
      title: (
        <span>
          <TrophyOutlined style={{ color: '#c0c0c0', marginRight: 4 }} />
          Серебро
        </span>
      ),
      dataIndex: 'silver',
      key: 'silver',
      width: 120,
      align: 'center',
      render: (silver: number) => (
        <span style={{ fontSize: '16px', fontWeight: 'bold' }}>
          <TrophyOutlined style={{ color: '#c0c0c0', marginRight: 4 }} />
          {silver}
        </span>
      ),
    },
    {
      title: (
        <span>
          <TrophyOutlined style={{ color: '#cd7f32', marginRight: 4 }} />
          Бронза
        </span>
      ),
      dataIndex: 'bronze',
      key: 'bronze',
      width: 120,
      align: 'center',
      render: (bronze: number) => (
        <span style={{ fontSize: '16px', fontWeight: 'bold' }}>
          <TrophyOutlined style={{ color: '#cd7f32', marginRight: 4 }} />
          {bronze}
        </span>
      ),
    },
    {
      title: 'Всего',
      dataIndex: 'total',
      key: 'total',
      width: 100,
      align: 'center',
      render: (total: number) => (
        <span style={{ fontSize: '16px', fontWeight: 'bold' }}>{total}</span>
      ),
    },
  ];

  if (loading) {
    return (
      <ResultsContainer>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <Spin size="large" />
        </div>
      </ResultsContainer>
    );
  }

  if (teamResults.length === 0) {
    return (
      <ResultsContainer>
        <Title level={3}>Результаты команды</Title>
        <Empty
          description="Результаты пока не доступны"
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      </ResultsContainer>
    );
  }

  return (
    <ResultsContainer>
      <Title level={3} style={{ marginBottom: 24 }}>
        <TrophyOutlined style={{ marginRight: 8 }} />
        Результаты команды
      </Title>
      
      <Table
        columns={columns}
        dataSource={teamResults}
        loading={loading}
        rowKey="teamName"
        pagination={false}
        style={{ fontSize: '16px' }}
      />
    </ResultsContainer>
  );
};

export default TeamResults;

